import { financeMayAccessRetailCatalogueProposal } from "./proposalStaffCapabilities";
import { canCreateManualTrancheInvoice } from "./manualTrancheInvoice";
import { canEditApprovedProposalPaymentTerms } from "./adminFinancePaymentTermsAccess";
import { normalizeUserRole } from "./userRoles";

/** Internal staff who may view company-wide proposal/customer operational data. */
export const INTERNAL_OPERATIONAL_ROLES = [
  "admin",
  "finance",
  "sales",
  "service",
  "programming",
  "installer",
] as const;

export type InternalOperationalRole = (typeof INTERNAL_OPERATIONAL_ROLES)[number];

/**
 * Shared authority equivalent to `proposals:read_all`.
 * Company-wide proposal read for Sales/Service surfaces — never grants mutation.
 * Includes Inventory/Procurement plus roles that already have legitimate global visibility.
 */
export const PROPOSALS_READ_ALL_ROLES = [
  ...INTERNAL_OPERATIONAL_ROLES,
  "inventory",
] as const;

export type ProposalsReadAllRole = (typeof PROPOSALS_READ_ALL_ROLES)[number];

export function isInternalOperationalStaff(role: string | null | undefined): boolean {
  const normalized = normalizeUserRole(role);
  return (INTERNAL_OPERATIONAL_ROLES as readonly string[]).includes(normalized);
}

/** Capability: proposals:read_all — one authority for nav, reads, and mutation guards. */
export function canReadAllProposals(role: string | null | undefined): boolean {
  const normalized = normalizeUserRole(role);
  return (PROPOSALS_READ_ALL_ROLES as readonly string[]).includes(normalized);
}

/**
 * Proposal operational view gate. Delegates to `canReadAllProposals`.
 * Does not grant revise/send/approve/VO/pipeline mutation rights.
 */
export function canViewProposalOperationally(role: string | null | undefined): boolean {
  return canReadAllProposals(role);
}

/**
 * Inventory/Procurement (and any future role) that may only view proposals.
 * Never infer mutation authority from read-all.
 */
export function isProposalReadOnlyRole(role: string | null | undefined): boolean {
  return normalizeUserRole(role) === "inventory";
}

export type ProposalContactContext = {
  proposalSalespersonId?: number | null;
  customerSalespersonId?: number | null;
};

export function isProposalSalespersonAssigned(ctx: Pick<ProposalContactContext, "proposalSalespersonId">): boolean {
  const id = Number(ctx.proposalSalespersonId ?? 0);
  return Number.isFinite(id) && id > 0;
}

/**
 * Assigned PIC / salesperson for commercial edits and contact viewing.
 * Admin always authorised. Proposal salesperson when assigned; otherwise customer PIC.
 */
export function isAssignedPicOrSalespersonForProposal(
  user: { id: number; role: string },
  ctx: ProposalContactContext,
): boolean {
  if (user.role === "admin") return true;
  if (isProposalSalespersonAssigned(ctx)) {
    return Number(ctx.proposalSalespersonId) === user.id;
  }
  return Number(ctx.customerSalespersonId ?? 0) === user.id;
}

export function canViewCustomerContactForProposal(
  user: { id: number; role: string },
  ctx: ProposalContactContext,
): boolean {
  return isAssignedPicOrSalespersonForProposal(user, ctx);
}

/** Revise / commercial edit — admin or assigned PIC/salesperson only (not finance-wide). */
export function canManageProposalCommercially(
  user: { id: number; role: string },
  header: { salespersonId?: number | null; proposalSource?: string | null },
  customer?: { salespersonId?: number | null } | null,
): boolean {
  if (isProposalReadOnlyRole(user.role)) return false;
  if (user.role === "admin") return true;
  if (user.role === "finance") {
    return (
      financeMayAccessRetailCatalogueProposal(header) &&
      isAssignedPicOrSalespersonForProposal(user, {
        proposalSalespersonId: header.salespersonId,
        customerSalespersonId: customer?.salespersonId,
      })
    );
  }
  return isAssignedPicOrSalespersonForProposal(user, {
    proposalSalespersonId: header.salespersonId,
    customerSalespersonId: customer?.salespersonId,
  });
}

export type ProposalOperatorContext = {
  commercialOwnerId?: number | null;
  contributorUserIds?: ReadonlyArray<number> | null;
  /** True when any active family member is Commercial Head locked (family-wide). */
  familyCommercialHeadLocked?: boolean | null;
  /** Authoritative locked Commercial Head proposal id when family is locked. */
  commercialHeadProposalId?: number | null;
};

/**
 * Who may work on a proposal (revise / save / send / Link B / VO / cancel).
 * Admin, commercial owner (`salespersonId`), or prior revision contributor only.
 * Unrelated salespeople are excluded — commercial ownership is separate from this.
 */
export function canOperateProposal(
  user: { id: number; role: string },
  params: ProposalOperatorContext,
): boolean {
  // proposals:read_all never implies operate/mutate.
  if (isProposalReadOnlyRole(user.role)) return false;
  if (user.role === "admin") return true;
  const ownerId = Number(params.commercialOwnerId ?? 0);
  if (Number.isFinite(ownerId) && ownerId > 0 && ownerId === user.id) return true;
  const contributors = params.contributorUserIds ?? [];
  return contributors.some((id) => Number(id) === user.id);
}

/** @deprecated Prefer canOperateProposal — same rule (kept for call-site clarity on revise paths). */
export function canReviseProposal(
  user: { id: number; role: string },
  params: ProposalOperatorContext,
): boolean {
  return canOperateProposal(user, params);
}

/** Explicit commercial-ownership transfer / commission / forecast accountability. */
export function canTransferCommercialOwnership(
  user: { id: number; role: string },
  commercialOwnerId?: number | null,
): boolean {
  if (user.role === "admin") return true;
  const ownerId = Number(commercialOwnerId ?? 0);
  return Number.isFinite(ownerId) && ownerId > 0 && ownerId === user.id;
}

/** Roles that may change proposal pipeline stage (including Mark Won). Matches `updatePipelineStage`. */
export const PROPOSAL_PIPELINE_STAGE_MANAGE_ROLES = ["sales", "admin", "finance"] as const;

/**
 * Who may update pipeline stage (PROSPECT / WON / LOST).
 * Admin and finance: company-wide. Sales: commercial owner only.
 */
export function canUpdateProposalPipelineStage(
  user: { id: number; role: string },
  commercialOwnerId?: number | null,
): boolean {
  if (user.role === "admin" || user.role === "finance") return true;
  if (user.role === "sales") {
    const ownerId = Number(commercialOwnerId ?? 0);
    return Number.isFinite(ownerId) && ownerId > 0 && ownerId === user.id;
  }
  return false;
}

export function canEditExpectedDelivery(
  user: { id: number; role: string },
  ctx: ProposalContactContext,
): boolean {
  return canViewCustomerContactForProposal(user, ctx);
}

export const CONTACT_MASK = "Restricted";

export function maskContactField(value: string | null | undefined): string | null {
  const t = String(value ?? "").trim();
  if (!t) return null;
  return CONTACT_MASK;
}

export type ProposalAccessFlags = {
  canViewContact: boolean;
  /** Work-on-proposal: revise / save / send / VO / cancel (owner | contributor | admin). */
  canOperate: boolean;
  /**
   * Open/save a normal -Rn proposal revision.
   * False when the revision family has an active Commercial Head (use VO instead).
   */
  canRevise: boolean;
  canSend: boolean;
  canCancel: boolean;
  /** Operator may attempt VO; lifecycle eligibility is separate (approved D-Tools + deposit). */
  canCreateVo: boolean;
  /** Family-wide: an active member is Commercial Head locked. */
  commercialHeadLocked: boolean;
  /** Locked Commercial Head id when `commercialHeadLocked` (for VO navigation). */
  commercialHeadProposalId: number | null;
  canTransferOwnership: boolean;
  /** Mark Won / pipeline stage updates (admin, finance, or sales commercial owner). */
  canUpdatePipelineStage: boolean;
  canEditExpectedDelivery: boolean;
  canCreateManualTrancheInvoice: boolean;
  /**
   * Admin/Finance company-wide: edit progressive payment terms on approved/WON
   * deals without revise/owner permissions.
   */
  canEditPaymentTerms: boolean;
  /**
   * True when the viewer has proposals:read_all but no operate/mutate rights
   * (e.g. Inventory/Procurement). UI shows Read-only access.
   */
  isReadOnlyViewer: boolean;
};

export function resolveProposalAccessFlags(
  user: { id: number; role: string },
  header: { salespersonId?: number | null; proposalSource?: string | null },
  customer?: { salespersonId?: number | null } | null,
  operatorContext?: ProposalOperatorContext | null,
): ProposalAccessFlags {
  const ctx: ProposalContactContext = {
    proposalSalespersonId: header.salespersonId,
    customerSalespersonId: customer?.salespersonId,
  };
  const opParams: ProposalOperatorContext = operatorContext
    ? {
        commercialOwnerId: operatorContext.commercialOwnerId ?? header.salespersonId,
        contributorUserIds: operatorContext.contributorUserIds,
        familyCommercialHeadLocked: operatorContext.familyCommercialHeadLocked,
        commercialHeadProposalId: operatorContext.commercialHeadProposalId,
      }
    : {
        commercialOwnerId: header.salespersonId,
        contributorUserIds: [],
        familyCommercialHeadLocked: false,
        commercialHeadProposalId: null,
      };
  // Read-only roles never receive mutation flags — even if ownership fields match by accident.
  if (isProposalReadOnlyRole(user.role)) {
    const commercialHeadLocked = Boolean(opParams.familyCommercialHeadLocked);
    const commercialHeadProposalId =
      commercialHeadLocked && Number(opParams.commercialHeadProposalId ?? 0) > 0
        ? Number(opParams.commercialHeadProposalId)
        : null;
    return {
      canViewContact: false,
      canOperate: false,
      canRevise: false,
      canSend: false,
      canCancel: false,
      canCreateVo: false,
      commercialHeadLocked,
      commercialHeadProposalId,
      canTransferOwnership: false,
      canUpdatePipelineStage: false,
      canEditExpectedDelivery: false,
      canCreateManualTrancheInvoice: false,
      canEditPaymentTerms: false,
      isReadOnlyViewer: true,
    };
  }

  const canOperate = canOperateProposal(user, opParams);
  const commercialHeadLocked = Boolean(opParams.familyCommercialHeadLocked);
  const commercialHeadProposalId =
    commercialHeadLocked && Number(opParams.commercialHeadProposalId ?? 0) > 0
      ? Number(opParams.commercialHeadProposalId)
      : null;
  const canUpdatePipeline = canUpdateProposalPipelineStage(user, opParams.commercialOwnerId);
  return {
    canViewContact: canViewCustomerContactForProposal(user, ctx),
    canOperate,
    canRevise: canOperate && !commercialHeadLocked,
    canSend: canOperate,
    canCancel: canOperate,
    canCreateVo: canOperate,
    commercialHeadLocked,
    commercialHeadProposalId,
    canTransferOwnership: canTransferCommercialOwnership(user, opParams.commercialOwnerId),
    canUpdatePipelineStage: canUpdatePipeline,
    canEditExpectedDelivery: canEditExpectedDelivery(user, ctx),
    canCreateManualTrancheInvoice: canCreateManualTrancheInvoice(user, header, customer),
    canEditPaymentTerms: canEditApprovedProposalPaymentTerms(user.role),
    isReadOnlyViewer: canReadAllProposals(user.role) && !canOperate && !canUpdatePipeline,
  };
}

export function shapeContactValue(
  user: { id: number; role: string },
  ctx: ProposalContactContext,
  value: string | null | undefined,
): string | null {
  if (canViewCustomerContactForProposal(user, ctx)) {
    const t = String(value ?? "").trim();
    return t || null;
  }
  return maskContactField(value);
}
